import queryString from 'querystring';

import React from 'react';

import { NavLink as RouterNavLink, useLocation, useHistory, Prompt } from 'react-router-dom';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from 'reactstrap/lib/Alert';
import Button from 'reactstrap/lib/Button';
import Fade from 'reactstrap/lib/Fade';
import Form from 'reactstrap/lib/Form';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import Nav from 'reactstrap/lib/Nav';
import NavItem from 'reactstrap/lib/NavItem';
import NavLink from 'reactstrap/lib/NavLink';
import Progress from 'reactstrap/lib/Progress';
import Spinner from 'reactstrap/lib/Spinner';

import { Personalization, VoucherPersonalization, OrderDetail, VoucherPersonalizationType } from '@ttstr/api';
import { useActions, useShallowEqualSelector, useToggle } from '@ttstr/utils';
import ValidationAlert from '@ttstr/components/Form/ValidationAlert';
import { updateOrder } from '@ttstr/actions';
import { AppState } from '@ttstr/reducers';

import { CustomerDataFields } from '../Customer';
import { PersonalisationDataFields } from '../Customer';
import Sticky from '../Sticky/Sticky';
import FormField from '../Form/FormField';
import { useShopConfig } from '../ShopConfig/ShopConfigContext';

interface C19FormValues {
  id: number;
  type: VoucherPersonalizationType;
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
  street: string;
  zip_code: string;
  city: string;
  country: string;
}

interface Props extends Personalization {
  orderId: string;
  xtoken: string;
}

const C19Personalization: React.FC<Props> = ({
  orderId,
  xtoken,
  all_done,
  would_change_to_online,
  personalizations,
}) => {
  const [modalOpen, toggleModalOpen, setModalOpen] = useToggle(!all_done);
  const firstUnpersonalizedPage = React.useMemo(() => personalizations.findIndex((i) => !i.personalized) + 1, [
    personalizations,
  ]);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const qs = queryString.parse(location.search.substring(1));
  const { page } = qs;
  const { orders } = useShallowEqualSelector(mapStateToProps);
  const defaultPage = firstUnpersonalizedPage || 1;
  const currentPage = Math.min(Number(page ?? defaultPage), personalizations.length);
  const currentItem = personalizations[currentPage - 1];

  const next = () => {
    if (all_done) return setModalOpen(false);
    history.replace(`?page=${firstUnpersonalizedPage || currentPage + 1}#modal`);
  };

  const toggle = () => {
    toggleModalOpen();
    history.replace('?');
  };

  const toggleIfDone = all_done ? toggle : null;

  return (
    <div className="c19-personalization">
      <ol className="pl-3">
        {personalizations.map((p) => (
          <li key={p.id}>
            {p.personalized ? (
              <span>
                {p.firstname} {p.lastname}
              </span>
            ) : (
              <span className="text-danger">{t('ORDERS.DETAIL.PERSONALIZATION.NOT_PERSONALIZED')}</span>
            )}
          </li>
        ))}
      </ol>
      <Button color={all_done ? 'secondary' : 'primary'} onClick={toggle}>
        <i className="fa fa-ballot-check mr-2" /> {t('ORDERS.DETAIL.PERSONALIZATION.EDIT')}
      </Button>

      <Modal isOpen={modalOpen} toggle={toggleIfDone} unmountOnClose={false}>
        <ModalHeader toggle={toggle}>{t('ORDERS.DETAIL.PERSONALIZATION.TITLE')}</ModalHeader>

        <ModalBody className="pb-0">
          {would_change_to_online && (
            <Alert color="danger">
              <h2 className="alert-heading h4">{t('ORDERS.DETAIL.PERSONALIZATION.ATTENTION')}</h2>
              <p>{t('ORDERS.DETAIL.PERSONALIZATION.WOULD_CHANGE_TO_ONLINE')}</p>
              <FormField
                type="checkbox"
                name="accept-change"
                /* TODO: translate next line */
                label="Ich habe verstanden und möchte meine Daten dennoch aktualisieren und somit meine Hard-Tickets zu Online-Tickets ändern."
              />
            </Alert>
          )}
          <Nav className="mb-2" tabs>
            {personalizations.map((personalization, i) => (
              <NavItem key={personalization.id}>
                <NavLink tag={RouterNavLink} to={`?page=${i + 1}#modal`} isActive={() => currentPage === i + 1}>
                  {personalization.firstname ? (
                    <span>{personalization.firstname}</span>
                  ) : (
                    <>
                      <span>{t('ORDERS.DETAIL.PERSONALIZATION.FORM', { n: i + 1 })}</span>
                    </>
                  )}
                  {personalization.personalized && (
                    <i
                      className="fa fa-check ml-2 text-success"
                      title={t(`ORDERS.DETAIL.PERSONALIZATION.PERSONALIZED`)}
                    />
                  )}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </ModalBody>
        <PagedPersonalizer
          item={currentItem}
          orderId={orderId}
          tickets={orders[Number(orderId)].items}
          xtoken={xtoken}
          currentPage={currentPage}
          pages={personalizations.length}
          wouldChangeToOnline={would_change_to_online}
          key={currentPage}
          next={next}
        />
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const { orders } = state.Orders;

  return {
    orders,
  };
};

interface PagedPersonalizerProps {
  orderId: string;
  xtoken: string;
  tickets: any;
  item: VoucherPersonalization;
  currentPage: number;
  pages: number;
  wouldChangeToOnline?: boolean;
  /**
   * continue to the next page / action
   */
  next?(): void;
}

const PagedPersonalizer: React.FC<PagedPersonalizerProps> = ({
  item,
  orderId,
  tickets,
  xtoken,
  currentPage,
  pages,
  wouldChangeToOnline,
  next,
}) => {
  const { t } = useTranslation();
  const { c19PersonalizationOverrideValidations } = useShopConfig();
  const { updateOrder } = useActions(mapC19DispatchToProps);
  const formContext = useForm<C19FormValues>({ mode: 'onChange', defaultValues: item });
  const { register, handleSubmit, formState } = formContext;
  const { isValid, isSubmitting, dirtyFields } = formState;
  const pendingChanges = Boolean(Object.keys(dirtyFields).length);

  const onSubmit: SubmitHandler<C19FormValues> = async (values) => {
    const orderPatch: Partial<OrderDetail> = {
      change_to_online: wouldChangeToOnline || undefined,
      personalization: {
        personalizations: [values],
      },
    };
    await updateOrder(orderPatch, orderId, xtoken);
    next();
  };

  return (
    <>
      <Prompt
        when={pendingChanges && !isSubmitting}
        message={t('ORDERS.DETAIL.PERSONALIZATION.UNSAVED_CHANGES_INFO')}
      />
      <FormProvider {...formContext}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Fade>
              <h2 className='h5'>{tickets.find((t) => t.variant.id === item.variant_id).full_title}</h2>
              <p dangerouslySetInnerHTML={{ __html: t('ORDERS.DETAIL.PERSONALIZATION.DESCRIPTION') }} />
              <input type="hidden" name="id" ref={register} />
              <input type="hidden" name="type" ref={register} />
              <PersonalisationDataFields
                mode="create"
                hideCompany
                hideAddressAddition
                hideState
                overrideValidations={
                  c19PersonalizationOverrideValidations ?? {
                    phone: {
                      ask: true,
                      required: true,
                    },
                  }
                }
              />
            </Fade>
          </ModalBody>
          <Sticky bottom when={isValid}>
            {pages > 1 && (
              <Progress className="flex-shrink-0" animated={isSubmitting} value={currentPage} max={pages}>
                {t('CART.X_OF_Y', { x: currentPage, y: pages })}
              </Progress>
            )}

            <ModalFooter>
              <ValidationAlert />
              <Button type="submit" color="primary" disabled={!isValid || isSubmitting}>
                {t(`ORDERS.DETAIL.PERSONALIZATION.${item.personalized ? 'UPDATE' : 'SAVE'}`)}
                {isSubmitting && <Spinner size="sm" className="ml-2" />}
              </Button>
            </ModalFooter>
          </Sticky>
        </Form>
      </FormProvider>
    </>
  );
};

const mapC19DispatchToProps = {
  updateOrder,
};

export default React.memo(C19Personalization);
